import React, { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Link as LinkIcon, FileText, Upload, X } from 'lucide-react';
import {
  ingestAgentKnowledge, ingestAgentDocument, listAgentKnowledge, removeAgentKnowledgeSource, clearAgentKnowledge,
  type KbUsage, type IngestKind,
} from '../../services/knowledgeService';
import { readFileAsText, readFileAsBase64, isTextFile, isDocFile, isImageFile, KB_UPLOAD_EXTS } from '../../utils/readTextFile';
import { formatBytes } from '../../utils/chatAttachments';
import { modalField, modalLabel } from '../ui/modalSpacing';
import { TYPE } from '../../constants/typeScale';

// A doc is uploaded base64-encoded (~1.34x its raw bytes). Cap the RAW file at ~7 MB so the encoded payload
// stays under the ~10 MB request-transport limit — otherwise a big file dies mid-upload with a cryptic error.
const MAX_DOC_UPLOAD_BYTES = 7 * 1024 * 1024;

interface Props {
  /** Stable key the knowledge is scoped to — a custom agent's id. */
  agentKey: string;
  /** Bubble errors up to the host modal (optional). */
  onError?: (msg: string) => void;
}

/**
 * Reusable knowledge-base editor for an agent: attach URL links, paste text/HTML, or upload documents
 * (.txt/.md/.csv/.json read locally; .pdf/.docx extracted server-side) that back the agent (retrieved
 * via RAG when it replies). Shows storage usage against the per-agent cap. Shared by the custom-agent
 * editor and the in-chat Agent Details panel.
 */
const AgentKnowledgeBase: React.FC<Props> = ({ agentKey, onError }) => {
  const [usage, setUsage] = useState<KbUsage | null>(null);
  const [kind, setKind] = useState<Exclude<IngestKind, 'document'>>('url');
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const fail = (m: string) => onError?.(m);

  useEffect(() => {
    if (!agentKey) return;
    listAgentKnowledge(agentKey).then(setUsage).catch(() => setUsage(null));
  }, [agentKey]);

  const add = async () => {
    if (!input.trim()) return;
    setBusy(true);
    try {
      setUsage(await ingestAgentKnowledge(agentKey, kind, input.trim()));
      setInput('');
    } catch (e: any) { fail(e?.message || 'Could not add that source'); } finally { setBusy(false); }
  };

  const remove = async (source: string) => {
    setBusy(true);
    try { setUsage(await removeAgentKnowledgeSource(agentKey, source)); }
    catch (e: any) { fail(e?.message || 'Could not remove that source'); } finally { setBusy(false); }
  };

  const clear = async () => {
    setBusy(true);
    try { setUsage(await clearAgentKnowledge(agentKey)); }
    catch (e: any) { fail(e?.message || 'Could not clear the knowledge base'); } finally { setBusy(false); }
  };

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const file of Array.from(files)) {
        // Guard the REQUEST size, not the KB cap: a doc is sent base64-encoded (~1.34× its raw bytes), so a file
        // near the ~10 MB transport limit would fail with a cryptic error. Cap raw at ~7 MB (→ ~9.4 MB encoded)
        // and say so clearly, rather than letting the upload die mid-transport.
        if (file.size > MAX_DOC_UPLOAD_BYTES) {
          fail(`"${file.name}" is too large (${formatBytes(file.size)}). Max ~7 MB per file — upload a smaller file or paste the key sections as text.`);
          continue;
        }
        if (isTextFile(file.name, file.type)) {
          const text = await readFileAsText(file);
          if (text.trim()) setUsage(await ingestAgentKnowledge(agentKey, 'text', text, file.name));
        } else if (isDocFile(file.name, file.type) || isImageFile(file.name, file.type)) {
          // Docs extract free; an image is read by the vision model and stored as knowledge.
          const b64 = await readFileAsBase64(file);
          setUsage(await ingestAgentDocument(agentKey, b64, file.type || 'application/octet-stream', file.name));
        } else {
          fail(`"${file.name}" isn't a supported file. Use text/CSV, PDF, Word, PowerPoint, Excel or an image.`);
        }
      }
    } catch (e: any) { fail(e?.message || 'Could not read that file'); } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const sources = usage?.sources || [];
  const pct = usage && usage.cap ? Math.min(100, Math.round((usage.bytes / usage.cap) * 100)) : 0;
  const full = !!usage && usage.remaining <= 0;

  return (
    <section>
      <p className={modalLabel}>Knowledge base</p>
      <p className={`mb-3 ${TYPE.control} text-secondary-400`}>
        Attach links, paste text/HTML, or upload documents and images to specialise this agent — it draws on them when it replies. Images are read for their text and visuals.
      </p>

      {usage && (
        <div className="mb-3">
          <div className={`flex items-center justify-between ${TYPE.meta} text-secondary-500 dark:text-secondary-400 mb-1`}>
            <span>{formatBytes(usage.bytes)} of {formatBytes(usage.cap)} used</span>
            {sources.length > 0 && (
              <button onClick={clear} disabled={busy} className="inline-flex items-center gap-1 hover:text-secondary-500">
                <Trash2 className="h-3 w-3" /> Clear all
              </button>
            )}
          </div>
          <div className="h-1.5 w-full rounded-full bg-secondary-200 dark:bg-secondary-700 overflow-hidden">
            <div className={`h-full ${full ? 'bg-secondary-800' : 'bg-secondary-800 dark:bg-secondary-200'}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      {sources.length > 0 && (
        <div className="mb-3 flex flex-col gap-1">
          {sources.map((s) => (
            <div key={s.source} className={`group inline-flex items-center gap-1.5 rounded-md bg-secondary-100 dark:bg-secondary-800 px-2 py-1 ${TYPE.control} text-secondary-600 dark:text-secondary-300`}>
              {/^https?:/.test(s.source) ? <LinkIcon className="h-3 w-3 shrink-0" /> : <FileText className="h-3 w-3 shrink-0" />}
              <span className="truncate flex-1" title={s.source}>{s.source}</span>
              <span className={`shrink-0 ${TYPE.micro} text-secondary-400`}>{formatBytes(s.bytes)}</span>
              <button onClick={() => remove(s.source)} disabled={busy} aria-label={`Remove ${s.source}`} title="Remove"
                className="shrink-0 text-secondary-400 hover:text-secondary-500"><X className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <select value={kind} onChange={(e) => setKind(e.target.value as Exclude<IngestKind, 'document'>)} className={`${modalField} w-24 shrink-0`}>
          <option value="url">URL</option>
          <option value="text">Text</option>
          <option value="html">HTML</option>
        </select>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
          placeholder={kind === 'url' ? 'https://…' : kind === 'html' ? 'Paste HTML' : 'Paste reference text'}
          className={modalField}
          disabled={full}
        />
        <button onClick={add} disabled={busy || !input.trim() || full} aria-label="Add source" title={full ? 'Knowledge base is full' : 'Add source'}
          className={`shrink-0 rounded-lg bg-secondary-900 dark:bg-white px-3 py-2.5 ${TYPE.body} font-medium text-white dark:text-secondary-900 disabled:opacity-40`}>
          <Plus className="h-4 w-4" />
        </button>
      </div>

      <input ref={fileRef} type="file" multiple accept={KB_UPLOAD_EXTS.join(',')} className="hidden" onChange={(e) => upload(e.target.files)} />
      <button onClick={() => fileRef.current?.click()} disabled={busy || full}
        className={`mt-2.5 w-full inline-flex items-center justify-center gap-2 rounded-lg border border-dashed border-secondary-300 dark:border-secondary-600 px-3 py-2.5 ${TYPE.control} text-secondary-500 hover:border-secondary-500 hover:text-secondary-700 dark:hover:text-secondary-200 disabled:opacity-40`}>
        <Upload className="h-3.5 w-3.5" /> {full ? 'Knowledge base is full' : 'Upload documents (.txt, .md, .csv, .json, .pdf, .docx)'}
      </button>
    </section>
  );
};

export default AgentKnowledgeBase;
